import * as yup from "yup";
import { CompanyInvitationDTO, Language } from "@/api/data-contracts";
import { useMemo } from "react";
import { languageOptions, salutationOptions } from "@/datacaches";
import { Constants } from "@/api/constants";
import { EMAIL_REGEX } from "@/regex";
import { useTranslation } from "react-i18next";
import { CompanyI18N, UserI18N } from "@/translations";

/**
 * Builds the company-invitation schema. `availableLanguages` restricts the `language` field's
 * `.oneOf(...)` validation to languages with a maintained/uploaded language file (see
 * `useAvailableLanguages` in `datacaches.ts`) — defaults to the static `languageOptions`
 * (baseline: GERMAN only) so existing callers without the dynamic dropdown keep working.
 * An empty `availableLanguages` (the cache's loading/error state) falls back to the same static
 * `languageOptions` baseline internally, so `.oneOf(...)` never validates against an empty set.
 */
export const useCompanyInvitationSchema = (availableLanguages: Language[] = languageOptions) => {
    const { t } = useTranslation();
    return useMemo(() => {
        // `useAvailableLanguages()` resolves to `[]` while loading and permanently after a
        // fetch error. An empty `.oneOf([])` would reject any value — including the pre-selected
        // GERMAN default — so fall back to the static baseline to never validate against an
        // empty set (same rationale as `useUserInvitationSchema` in company-page/userInvitationSchema.ts).
        const languagesForValidation = availableLanguages.length > 0 ? availableLanguages : languageOptions;
        return yup.object().shape({
            email: yup
                .string()
                .required(t(UserI18N.form.validation.emailRequired))
                .min(
                    Constants.EMAIL_LENGTH_MIN,
                    t(UserI18N.form.validation.emailMin, { min: Constants.EMAIL_LENGTH_MIN })
                )
                .max(
                    Constants.EMAIL_LENGTH_MAX,
                    t(UserI18N.form.validation.emailMax, { max: Constants.EMAIL_LENGTH_MAX })
                )
                .matches(EMAIL_REGEX, t(UserI18N.form.validation.emailInvalid)),

            salutation: yup
                .string()
                .required(t(UserI18N.form.validation.salutationRequired))
                .oneOf(salutationOptions, t(UserI18N.form.validation.salutationInvalid)),

            firstName: yup
                .string()
                .required(t(UserI18N.form.validation.firstNameRequired))
                .min(
                    Constants.FIRSTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.firstNameMin, { min: Constants.FIRSTNAME_LENGTH_MIN })
                )
                .max(
                    Constants.FIRSTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.firstNameMax, { max: Constants.FIRSTNAME_LENGTH_MAX })
                ),

            lastName: yup
                .string()
                .required(t(UserI18N.form.validation.lastNameRequired))
                .min(
                    Constants.LASTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.lastNameMin, { min: Constants.LASTNAME_LENGTH_MIN })
                )
                .max(
                    Constants.LASTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.lastNameMax, { max: Constants.LASTNAME_LENGTH_MAX })
                ),

            companyName: yup
                .string()
                .required(t(CompanyI18N.form.validation.nameRequired))
                .min(
                    Constants.COMPANY_NAME_LENGTH_MIN,
                    t(CompanyI18N.form.validation.nameMin, { min: Constants.COMPANY_NAME_LENGTH_MIN })
                )
                .max(
                    Constants.COMPANY_NAME_LENGTH_MAX,
                    t(CompanyI18N.form.validation.nameMax, { max: Constants.COMPANY_NAME_LENGTH_MAX })
                ),

            language: yup
                .string()
                .required(t(UserI18N.form.validation.languageRequired))
                .oneOf(languagesForValidation, t(UserI18N.form.validation.languageRequired))
        }) as yup.ObjectSchema<CompanyInvitationDTO>;
    }, [t, availableLanguages]);
};
